import { CustomSelect } from '@/components/CustomSelect';

import type { TaxonomyOption } from '../../constants/taxonomy';

interface MultiSelectFieldProps {
  /** The field's display name; also the control's accessible name. */
  label: string;
  value: readonly string[];
  options: readonly TaxonomyOption[];
  onChange: (next: string[]) => void;
  /** The drawer's selection disagrees on this field. */
  mixed?: boolean;
  disabled?: boolean;
}

/**
 * A searchable multi-select over a vocabulary list, for the long lists
 * (nameplates, carline codes) a checkbox group would turn into a wall.
 *
 * A thin layer over `CustomSelect`, which the filter drawer already uses in
 * this mode: it takes and returns `string[]` where the shared component speaks
 * comma-joined strings, and it shows "Mixed" the way the form's other controls
 * do. Values are vocabulary tokens, none of which contain a comma.
 *
 * Every stored value is assumed to be in `options`: retiring a token from the
 * backend vocabulary ships with a data migration that rewrites the rows holding
 * it (see `vocabulary.py`). A value outside the list would be sent back with
 * every edit and the backend would refuse the whole list.
 */
export function MultiSelectField({
  label,
  value,
  options,
  onChange,
  mixed = false,
  disabled = false,
}: MultiSelectFieldProps) {
  const labelOf = (token: string) => options.find((o) => o.value === token)?.label ?? token;
  return (
    <div
      role="group"
      aria-label={label}
      className={`rounded-md ${mixed ? 'ring-1 ring-amber-200 bg-amber-50/40' : ''}`}
    >
      <CustomSelect
        label={label}
        showLabel={false}
        placeholder={mixed && value.length === 0 ? 'Mixed' : '—'}
        value={value.join(',')}
        options={options.map((o) => o.value)}
        onChange={(joined) => onChange(joined ? joined.split(',') : [])}
        multi
        searchable
        sortSelectedFirst
        disabled={disabled}
        renderOption={labelOf}
        renderMultiDisplay={(selected) => selected.map(labelOf).join(', ')}
      />
    </div>
  );
}
